'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
    $getSelection,
    $isRangeSelection,
    $createTextNode,
    $createParagraphNode,
    COMMAND_PRIORITY_HIGH,
    KEY_ESCAPE_COMMAND,
} from 'lexical'
import { $createMentionNode } from './MentionNode'
import { $getContextAtCursor } from './editorContext'
import MentionDropdown from './MentionDropdown'

const BLOCKED_CONTEXTS = new Set(['description', 'character', 'segment', 'scene'])

function getQueryAndRange(editor) {
    return editor.getEditorState().read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null

        const anchor = selection.anchor
        if (anchor.type !== 'text') return null

        const anchorNode = anchor.getNode()
        const text = anchorNode.getTextContent().slice(0, anchor.offset)

        const triggerIndex = text.lastIndexOf('#')
        if (triggerIndex === -1) return null

        const charBefore = triggerIndex > 0 ? text[triggerIndex - 1] : ' '
        if (charBefore !== ' ' && charBefore !== '\n' && triggerIndex !== 0) return null

        const query = text.slice(triggerIndex + 1)
        if (query.includes(' ') && query.length > 20) return null

        return { query, atIndex: triggerIndex, anchorOffset: anchor.offset, anchorKey: anchor.key }
    })
}

export default function CharacterMentionPlugin({ characters = [] }) {
    const [editor] = useLexicalComposerContext()
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [anchorRect, setAnchorRect] = useState(null)
    const matchRef = useRef(null)

    const filteredCharacters = characters
        .filter(char =>
            char.name.toLowerCase().includes(query.toLowerCase())
        )
        .map(char => ({
            id: char._id || char.id,
            label: char.name,
            description: char.description,
            data: {},
        }))

    const updateDropdownPosition = useCallback(() => {
        const domSelection = window.getSelection()
        if (!domSelection || domSelection.rangeCount === 0) return

        const range = domSelection.getRangeAt(0)
        const rect = range.getBoundingClientRect()
        setAnchorRect(rect)
    }, [])

    const close = useCallback(() => {
        setIsOpen(false)
        setQuery('')
        matchRef.current = null
    }, [])

    const handleSelect = useCallback((item) => {
        editor.update(() => {
            const match = matchRef.current
            if (!match) return

            const selection = $getSelection()
            if (!$isRangeSelection(selection)) return

            const anchor = selection.anchor
            const anchorNode = anchor.getNode()
            const text = anchorNode.getTextContent()

            const before = text.slice(0, match.atIndex)
            const after = text.slice(match.anchorOffset)

            anchorNode.setTextContent(before)

            const mentionNode = $createMentionNode(
                'character',
                item.id,
                item.label,
                item.data || {}
            )

            const placeAfter = (anchorOfMention) => {
                const nextParagraph = $createParagraphNode()
                if (after.length > 0) {
                    const trailingText = $createTextNode(after)
                    nextParagraph.append(trailingText)
                    anchorOfMention.insertAfter(nextParagraph)
                    trailingText.select(0, 0)
                } else {
                    anchorOfMention.insertAfter(nextParagraph)
                    nextParagraph.selectStart()
                }
            }

            if (before.length > 0) {
                const parentParagraph = anchorNode.getParent()

                const mentionParagraph = $createParagraphNode()
                mentionParagraph.append(mentionNode)
                parentParagraph.insertAfter(mentionParagraph)

                placeAfter(mentionParagraph)
            } else {
                anchorNode.replace(mentionNode)
                placeAfter(mentionNode.getParent())
            }
        })
        close()
    }, [editor, close])

    useEffect(() => {
        return editor.registerCommand(
            KEY_ESCAPE_COMMAND,
            () => {
                if (isOpen) {
                    close()
                    return true
                }
                return false
            },
            COMMAND_PRIORITY_HIGH
        )
    }, [editor, isOpen, close])

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const context = $getContextAtCursor()
                if (BLOCKED_CONTEXTS.has(context)) {
                    if (isOpen) close()
                    return
                }

                const result = getQueryAndRange(editor)
                if (result) {
                    matchRef.current = result
                    setQuery(result.query)
                    setIsOpen(true)
                    setTimeout(updateDropdownPosition, 0)
                } else {
                    if (isOpen) close()
                }
            })
        })
    }, [editor, isOpen, close, updateDropdownPosition])

    if (!isOpen) return null

    return (
        <MentionDropdown
            anchorRect={anchorRect}
            items={filteredCharacters}
            onSelect={handleSelect}
            onClose={close}
            emptyMessage="No characters found"
        />
    )
}
